<!-- src/views/admin/AdminUsefulLinksView.vue -->
<template>
  <div class="p-4 sm:p-6 md:p-8 bg-[#F0F0F0] min-h-screen relative font-sans selection:bg-[#20FCF1]/30 text-left">
    <div class="max-w-7xl mx-auto pb-24 text-left">

      <!-- HEADER & ACTIONS: Midnight Premium Standard -->
      <div class="flex flex-col xl:flex-row items-start xl:items-center justify-between mb-12 gap-6 bg-[#0A1630] p-10 rounded-[40px] shadow-2xl border-b-4 border-[#20FCF1] animate-fadeIn">
        <div>
          <h1 class="text-3xl font-black text-white uppercase tracking-tight">Resource & Knowledge Hub</h1>
          <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-3">Configure Dynamic Knowledge Cards • Sentinel Safe Premium</p>
        </div>
        
        <div class="flex flex-wrap gap-3 w-full xl:w-auto items-center">
             <!-- Create Card Button -->
             <button @click="createNewCard" class="px-6 py-3.5 bg-[#20FCF1] text-[#0A1630] rounded-2xl font-black uppercase text-[10px] tracking-widest hover:scale-105 transition-all shadow-lg flex items-center gap-2 border-none cursor-pointer">
                <PlusIcon class="h-4 w-4" /> <span>Create Card</span>
             </button>
             
             <!-- Preview Button -->
             <router-link v-slot="{ href, navigate }" to="/useful-links" custom>
               <a :href="href" @click="navigate" class="px-6 py-3.5 bg-white/10 border border-white/10 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-white/20 transition-all flex items-center gap-2 shadow-lg no-underline">
                 <EyeIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Preview Page</span>
               </a>
             </router-link>

             <!-- Return Button -->
             <router-link v-slot="{ href, navigate }" to="/client-admin" custom>
               <a :href="href" @click="navigate" class="px-6 py-3.5 bg-white/5 border border-white/10 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-white/10 transition-all flex items-center gap-2 no-underline">
                 <ArrowUturnLeftIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Return</span>
               </a>
             </router-link>
        </div>
      </div>

      <div v-if="loading" class="flex flex-col items-center justify-center py-32 animate-fadeIn">
        <div class="w-12 h-12 border-4 border-[#0A1630] border-t-[#20FCF1] rounded-full animate-spin"></div>
        <p class="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 mt-6">Decrypting Resource Configuration...</p>
      </div>
      
      <div v-else-if="error" class="bg-rose-50 border-2 border-[#EF4444]/20 p-10 rounded-[40px] text-center text-[#EF4444] font-black uppercase tracking-widest text-xs animate-fadeIn">
        Error: {{ error }}
      </div>

      <!-- EMPTY STATE -->
      <div v-else-if="resources.length === 0" class="text-center py-24 bg-slate-50 rounded-[40px] border border-dashed border-slate-200 animate-fadeIn shadow-inner">
         <PlusIcon class="h-14 w-14 text-slate-200 mx-auto mb-6 opacity-30 cursor-pointer" @click="createNewCard" />
         <h3 class="text-xl font-black text-[#0A1630] uppercase tracking-tight">Library Empty</h3>
         <p class="text-xs font-bold text-slate-400 uppercase tracking-widest mt-2 mb-6">No custom resource cards have been initialised yet.</p>
         <button @click="createNewCard" class="px-6 py-3.5 bg-[#0A1630] text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-800 transition-all border-none cursor-pointer">
           Create First Card
         </button>
      </div>

      <!-- 🛡️ LINK CARDS GRID: Standardised 3-Wide Responsive Card Layout -->
      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 animate-fadeIn">
        
        <div 
          v-for="(resource, index) in resources" 
          :key="resource.id" 
          class="bg-white rounded-[32px] shadow-lg border border-slate-100 overflow-hidden flex flex-col hover:border-[#20FCF1]/30 hover:shadow-2xl transition-all duration-300"
        >
          <!-- Card Header -->
          <div class="p-6 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
             <h2 class="text-base font-black text-[#0A1630] uppercase tracking-tight flex items-center gap-2.5">
               <span class="flex items-center justify-center h-7 w-7 rounded-full bg-[#0A1630] text-[#20FCF1] text-[10px] font-black">{{ index + 1 }}</span>
               Card configuration
             </h2>
             <button @click="deleteCard(index)" class="p-2 bg-rose-50 text-rose-500 rounded-xl hover:bg-rose-500 hover:text-white transition-all cursor-pointer border-none" title="Delete Card">
                <TrashIcon class="h-4 w-4" />
             </button>
          </div>

          <!-- Card Body -->
          <div class="p-6 space-y-6 flex-grow text-left">
            
            <!-- Metadata & Title -->
            <div class="space-y-4 text-left">
              <div>
                <label :for="`title-${index}`" class="micro-label mb-2 block ml-1 text-left">Card Title <span class="text-rose-500">*</span></label>
                <input type="text" :id="`title-${index}`" v-model="resource.title" placeholder="e.g. Employee Handbook" class="form-input">
              </div>

              <!-- Card Type Select (Link / Document / Information Mode) -->
              <div>
                <label :for="`mode-${index}`" class="micro-label mb-2 block ml-1 text-left">Card Mode selection</label>
                <select :id="`mode-${index}`" v-model="resource.mode" class="form-select">
                  <option value="link">Hyperlink Mode (Redirect URL)</option>
                  <option value="document">Document Mode (File Download)</option>
                  <option value="info">Info Mode (Display Text/Briefing)</option>
                </select>
              </div>

              <!-- Card Description (Description field added for text flexibility) -->
              <div>
                <label :for="`desc-${index}`" class="micro-label mb-2 block ml-1 text-left">Card Description / Briefing</label>
                <textarea :id="`desc-${index}`" v-model="resource.description" placeholder="A brief description of this resource or a quick information bulletin." class="form-textarea h-24"></textarea>
              </div>

              <!-- Badge Label Configuration -->
              <div>
                <label :for="`badge-${index}`" class="micro-label mb-2 block ml-1 text-left">Badge Label / Tag</label>
                <input type="text" :id="`badge-${index}`" v-model="resource.badgeText" placeholder="e.g. UPDATED or PDF" class="form-input" />
              </div>

              <!-- Link URL configuration (Only visible for hyperlink mode) -->
              <div v-if="resource.mode === 'link'">
                <label :for="`url-${index}`" class="micro-label mb-2 block ml-1 text-left">Link URL (Optional)</label>
                <input type="text" :id="`url-${index}`" v-model="resource.url" placeholder="https://" class="form-input">
              </div>
            </div>

            <!-- Uploads -->
            <div class="space-y-4 pt-4 border-t border-slate-100 text-left">
               <!-- Image Upload -->
               <div class="text-left">
                  <label class="micro-label mb-2 block ml-1 text-left">Card Image (Visual Backdrop)</label>
                  
                  <div v-if="resource.imageUrl" class="relative group mb-3 rounded-2xl overflow-hidden border border-slate-100 shadow-sm">
                      <img :src="resource.imageUrl" class="h-28 w-full object-cover" alt="Preview">
                      <button @click="removeFile(index, 'image')" class="absolute top-2 right-2 bg-rose-600 text-white p-2 rounded-xl shadow-lg hover:bg-rose-700 opacity-0 group-hover:opacity-100 transition-opacity border-none cursor-pointer" title="Remove Image">
                          <TrashIcon class="h-4 w-4" />
                      </button>
                  </div>
                  
                  <label :for="`image-${index}`" class="cursor-pointer flex items-center justify-center w-full px-5 py-3 border border-slate-200 rounded-2xl shadow-inner text-xs font-black uppercase tracking-wider text-slate-600 bg-slate-50 hover:bg-slate-100 transition-colors">
                    <PhotoIcon class="h-5 w-5 mr-2 text-slate-400" /> {{ resource.imageUrl ? 'Change Image' : 'Upload Image' }}
                  </label>
                  <input type="file" :id="`image-${index}`" @change="handleFileUpload($event, index, 'image')" accept="image/*" class="hidden">
               </div>

               <!-- Document Upload (Only visible in Document Mode) -->
               <div v-if="resource.mode === 'document'" class="text-left">
                  <label class="micro-label mb-2 block ml-1 text-left">Document File</label>
                  
                  <div v-if="resource.documentUrl" class="mb-3 p-4 bg-emerald-50 rounded-2xl border border-emerald-100 flex items-center justify-between text-left">
                     <span class="text-xs text-emerald-800 font-bold truncate mr-2">{{ resource.documentName || 'File Uploaded' }}</span>
                     <button @click="removeFile(index, 'document')" class="text-rose-500 hover:text-rose-700 bg-transparent border-none cursor-pointer">
                        <TrashIcon class="h-4 w-4" />
                     </button>
                  </div>
                  
                  <label :for="`doc-${index}`" class="cursor-pointer flex items-center justify-center w-full px-5 py-3 border border-slate-200 rounded-2xl shadow-inner text-xs font-black uppercase tracking-wider text-slate-600 bg-slate-50 hover:bg-slate-100 transition-colors">
                     <PaperClipIcon class="h-5 w-5 mr-2 text-slate-400" /> {{ resource.documentUrl ? 'Change File' : 'Upload File' }}
                  </label>
                  <input type="file" :id="`doc-${index}`" @change="handleFileUpload($event, index, 'document')" class="hidden">
               </div>
            </div>

          </div>
          
           <!-- Card Footer -->
           <div class="p-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
              <span class="text-[9px] font-black uppercase tracking-wider pl-2" :class="saveStatus[index]?.success ? 'text-emerald-600' : (saveStatus[index] ? 'text-rose-600' : 'text-slate-400')">
                  {{ saveStatus[index] ? saveStatus[index].message : 'Unsaved changes' }}
              </span>
              <button @click="saveResourceCard(index)" :disabled="isSaving[index]" class="bg-[#0A1630] text-white py-3 px-6 rounded-xl hover:bg-slate-800 transition font-black uppercase text-[10px] tracking-widest border-none cursor-pointer disabled:opacity-50">
                {{ isSaving[index] ? 'Saving...' : 'Save Card' }}
              </button>
            </div>

        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue';
import { db, storage } from '@/firebaseConfig';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { EyeIcon, TrashIcon, PhotoIcon, PaperClipIcon, PlusIcon, ArrowUturnLeftIcon } from '@heroicons/vue/24/outline';
import { useUserStore } from '@/stores/userStore';

const userStore = useUserStore();

const loading = ref(true);
const error = ref(null);
const resources = ref([]);
const isSaving = ref([]);
const saveStatus = ref([]);

const fileUploads = ref({}); 

// 🛡️ RECONCILED IDENTITY RESOLUTION: Secure reactive client tracking prevents Super Admin context crashes
const currentClientId = computed(() => userStore.clientId || userStore.userProfile?.clientId);

const fetchResources = async (clientId) => {
    currentClientId.value = clientId;
    loading.value = true;
    error.value = null;
    try {
        const clientDocRef = doc(db, 'clients', clientId);
        const docSnap = await getDoc(clientDocRef);
        if (docSnap.exists()) {
            const clientData = docSnap.data();
            const savedResources = clientData.resources || [];
            
            // Map saved arrays to highly flexible configuration objects
            resources.value = savedResources.map(res => reactive({
                id: res.id || Date.now().toString() + Math.random().toString(),
                title: res.title || '',
                url: res.url || '',
                imageUrl: res.imageUrl || '',
                documentUrl: res.documentUrl || '',
                documentName: res.documentName || '',
                imagePath: res.imagePath || '',
                documentPath: res.documentPath || '',
                description: res.description || '',
                badgeText: res.badgeText || '',
                mode: res.mode || 'link'
            }));

            // Hydrate save state arrays
            isSaving.value = Array(resources.value.length).fill(false);
            saveStatus.value = Array(resources.value.length).fill(null);
        }
    } catch (err) {
        error.value = "Failed to load useful links configuration.";
        console.error(err);
    } finally {
        loading.value = false;
    }
};

const createNewCard = () => {
  const newCard = {
    id: Date.now().toString() + Math.random().toString(),
    title: 'New Resource Card',
    mode: 'link', 
    url: '',
    imageUrl: '',
    documentUrl: '',
    documentName: '',
    imagePath: '',
    documentPath: '',
    description: '',
    badgeText: 'New'
  };
  resources.value.push(reactive(newCard));
  isSaving.value.push(false);
  saveStatus.value.push(null);
};

const deleteCard = async (index) => {
  if (!confirm("Are you sure you want to delete this resource card?")) return;
  const resource = resources.value[index];
  
  // Clean up any files from storage first
  if (resource.imagePath) {
    try { await deleteObject(storageRef(storage, resource.imagePath)); } catch (e) { console.error(e); }
  }
  if (resource.documentPath) {
    try { await deleteObject(storageRef(storage, resource.documentPath)); } catch (e) { console.error(e); }
  }
  
  resources.value.splice(index, 1);
  isSaving.value.splice(index, 1);
  saveStatus.value.splice(index, 1);

  await saveAllResources();
};

const saveAllResources = async () => {
    if (!currentClientId.value) return;
    try {
        const clientDocRef = doc(db, 'clients', currentClientId.value);
        const plainResources = resources.value.map(res => ({ ...res }));
        await updateDoc(clientDocRef, {
            resources: plainResources
        });
    } catch (err) {
        console.error("Error bulk-saving resources:", err);
    }
};

const handleFileUpload = (event, index, type) => {
    const file = event.target.files[0];
    if (!file) return;
    fileUploads.value[`${index}-${type}`] = file;

    if (type === 'image') {
        resources.value[index].imageUrl = URL.createObjectURL(file);
    } else {
        resources.value[index].documentName = file.name;
        resources.value[index].documentUrl = 'pending_upload'; 
    }
    saveStatus.value[index] = null;
};

const removeFile = async (index, type) => {
    const resource = resources.value[index];
    const pathKey = type === 'image' ? 'imagePath' : 'documentPath';
    const urlKey = type === 'image' ? 'imageUrl' : 'documentUrl';
    const nameKey = type === 'image' ? null : 'documentName';
    
    if (resource[pathKey]) {
        try {
            await deleteObject(storageRef(storage, resource[pathKey]));
        } catch (err) {
            console.error("Error deleting file from storage:", err);
        }
    }

    resource[urlKey] = '';
    resource[pathKey] = '';
    if (nameKey) resource[nameKey] = '';
    fileUploads.value[`${index}-${type}`] = null;
    
    await saveResourceCard(index);
};

const saveResourceCard = async (index) => {
    if (!currentClientId.value) {
        error.value = "Client ID not found. Cannot save.";
        return;
    }
    isSaving.value[index] = true;
    saveStatus.value[index] = null;

    try {
        const resource = resources.value[index];
        const imageFile = fileUploads.value[`${index}-image`];
        const docFile = fileUploads.value[`${index}-document`];

        if (imageFile) {
            const path = `clients/${currentClientId.value}/resources/card_${resource.id}/image/${imageFile.name}`;
            resource.imageUrl = await uploadFile(imageFile, path);
            resource.imagePath = path;
        }
        if (docFile) {
            const path = `clients/${currentClientId.value}/resources/card_${resource.id}/document/${docFile.name}`;
            resource.documentUrl = await uploadFile(docFile, path);
            resource.documentPath = path;
            resource.documentName = docFile.name;
        }
        
        await saveAllResources();
        saveStatus.value[index] = { success: true, message: 'Saved successfully!' };
        
        fileUploads.value[`${index}-image`] = null;
        fileUploads.value[`${index}-document`] = null;

    } catch (err) {
        saveStatus.value[index] = { success: false, message: `Save failed: ${err.message}` };
        console.error("Error saving resource card:", err);
    } finally {
        isSaving.value[index] = false;
        setTimeout(() => { 
            if (saveStatus.value[index]?.success) saveStatus.value[index] = null; 
        }, 3000);
    }
};

const uploadFile = (file, path) => {
  return new Promise((resolve, reject) => {
    const fileRef = storageRef(storage, path);
    const uploadTask = uploadBytesResumable(fileRef, file);

    uploadTask.on('state_changed', 
      () => {}, 
      (error) => { reject(error); }, 
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        resolve(downloadURL);
      }
    );
  });
};

// 🛡️ RECONCILED SYNC: Watches computed client ID reactively to resolve race condition crashes and load resource array
watch(currentClientId, (newId) => {
  if (newId) {
    fetchResources(newId);
  }
}, { immediate: true });
</script>

<style scoped>
.micro-label { @apply text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2 block ml-1 text-left; }
.form-input { @apply block w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold uppercase transition-all focus:ring-2 focus:ring-[#20FCF1] outline-none shadow-inner; }
.form-textarea { @apply block w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold uppercase transition-all focus:ring-2 focus:ring-[#20FCF1] outline-none shadow-inner resize-none; }
.form-select {
  @apply block w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold uppercase transition-all focus:ring-2 focus:ring-[#20FCF1] outline-none shadow-inner cursor-pointer appearance-none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%230A1630'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1.25rem center;
  background-size: 1.25rem;
}
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
.custom-scrollbar::-webkit-scrollbar { width: 4px; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #20FCF1; border-radius: 10px; }
</style>